import "./App.css";
import "./Startup.css";
import "./NetOverlay.css";
import { ChangeEvent, useEffect, useState } from "react";
import { PlayModule, lastInitStage, probeBrowser } from "./PlayModule";
import { useAppDispatch, useAppSelector, bootFile, init } from "./Actions";
import Mm2ControlPanel from "./Mm2ControlPanel";

function Stats() {
  const [fps, setFps] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (PlayModule === null) return;
      const frames = PlayModule.getFrames();
      PlayModule.clearStats();
      setFps(frames);
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  return <span className="stats">{fps} f/s</span>;
}

function App() {
  const state = useAppSelector((current) => current.play);
  const dispatch = useAppDispatch();
  const [selectedName, setSelectedName] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [stage, setStage] = useState(lastInitStage);
  const [probe, setProbe] = useState(() => probeBrowser());

  useEffect(() => {
    const listener = (event: Event) => {
      const custom = event as CustomEvent<string>;
      setStage(custom.detail || lastInitStage);
      setProbe(probeBrowser());
    };
    window.addEventListener("genie-play-init-stage", listener);
    return () => window.removeEventListener("genie-play-init-stage", listener);
  }, []);

  const ready = PlayModule !== null && ["initialized", "loading-game", "loaded"].includes(state.value);
  const starting = state.value === "starting";
  const failed = state.value === "failed";

  useEffect(() => {
    if (!pendingFile) return;
    if (PlayModule === null || state.value !== "initialized") return;
    const file = pendingFile;
    setPendingFile(null);
    dispatch(bootFile(file));
  }, [dispatch, pendingFile, state.value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedName(file.name);
    if (PlayModule !== null && ["initialized", "loaded"].includes(state.value)) {
      dispatch(bootFile(file));
      return;
    }
    setPendingFile(file);
    if (!starting) {
      setProbe(probeBrowser());
      dispatch(init());
    }
  };

  const pthreadReady = probe.crossOriginIsolated && probe.sharedArrayBuffer && probe.sharedWasmMemory;
  const baseGameOnly = new URLSearchParams(window.location.search).get("baseGame") === "1";

  return (
    <main className="bridge-shell">
      {ready && <Mm2ControlPanel />}

      <header className="bridge-shell__header">
        <div>
          <h1>Metropolismania 2 · GenieWoman NET Conversion</h1>
          <p>MM2 re-skinned and progressively rewritten as GenieWoman.EXE's persistent NET city.</p>
        </div>
        <div className="bridge-shell__telemetry">
          {ready ? <Stats /> : <span>Play! offline</span>}
          <span>{state.value}</span>
        </div>
      </header>

      <div className="net-status-strip">
        <strong>{baseGameOnly ? "BASE GAME BYPASS" : "GENIEWOMAN VISUAL PACK v1"}</strong>
        <span>NET presentation layer active</span>
        <span>Character 1 / Face 1 authored patch active</span>
      </div>

      <section className="disc-loader">
        <label className="disc-loader__button">
          Jack MM2 Into the NET
          <input type="file" onChange={handleChange} />
        </label>
        <div className="disc-loader__file">{selectedName || "Choose Metropolismania 2 (USA).bin"}</div>
        <div className="disc-loader__privacy">
          {pendingFile
            ? "Game selected. Starting the PS2 runtime, applying the Genie visual pack, then booting automatically…"
            : "The Genie visual pack is applied locally in your browser. Add ?baseGame=1 only when you deliberately want an unmodified comparison boot."}
        </div>
      </section>

      {!ready && (
        <section className={`startup-panel ${failed ? "startup-panel--failed" : ""}`}>
          <div className="startup-panel__copy">
            <strong>{starting ? "Opening the NET gate…" : failed ? "Play! did not initialize" : "Emulator status"}</strong>
            <p>{state.message}</p>
          </div>

          <button
            className="startup-panel__start"
            disabled={starting}
            onClick={() => {
              setProbe(probeBrowser());
              dispatch(init());
            }}
          >
            {starting ? "Starting…" : failed ? "Try Again" : "Start Emulator Without Game"}
          </button>

          <dl className="startup-panel__diagnostics">
            <div><dt>Current stage</dt><dd>{stage}</dd></div>
            <div><dt>WebAssembly</dt><dd>{probe.webAssembly ? "yes" : "NO"}</dd></div>
            <div><dt>WebGL 2</dt><dd>{probe.webgl2 ? "yes" : "NO"}</dd></div>
            <div><dt>Cross-origin isolated</dt><dd>{probe.crossOriginIsolated ? "yes" : "NO"}</dd></div>
            <div><dt>SharedArrayBuffer</dt><dd>{probe.sharedArrayBuffer ? "yes" : "NO"}</dd></div>
            <div><dt>Shared WASM memory</dt><dd>{probe.sharedWasmMemory ? "yes" : "NO"}</dd></div>
            <div><dt>Secure page</dt><dd>{probe.secureContext ? "yes" : "NO"}</dd></div>
            <div><dt>Visibility</dt><dd>{probe.visibility}</dd></div>
          </dl>

          {!pthreadReady && (
            <p className="startup-panel__mobile-note">
              Play! uses WebAssembly pthreads. It cannot start until Cross-origin isolated, SharedArrayBuffer, and Shared WASM memory all say yes. If this page is inside ChatGPT, open it in full Safari and reload before trying again.
            </p>
          )}

          {pthreadReady && (
            <p className="startup-panel__mobile-note">
              The threading prerequisites are available. If startup fails now, Current stage will tell us whether the failure is downloading, compiling, starting pthread workers, or creating the PS2 VM.
            </p>
          )}

          <details className="startup-panel__ua">
            <summary>Browser details</summary>
            <code>{probe.userAgent}</code>
          </details>
        </section>
      )}

      <footer className="bridge-shell__footer">
        This experiment contains no Metropolismania 2 game data. Load a game image you are authorized to use.
      </footer>
    </main>
  );
}

export default App;
